import React, { createContext, useContext, useState } from 'react';

import { useClassroomInterruption, useRestorePlayerFocus } from 'Util/Hooks';
import { useConfig } from 'Context/ConfigContext';
import { LAYOUT_TYPE } from 'Context/ClassroomContext';
import { useLearnCraftVideoPlayer } from 'Context/LearnCraftVideoPlayer';
import { useFaqTracking } from 'Util/Analytics/useFaqTracking';

import useMyExternalProfile from '../../../../Domain/useMyExternalProfile';

const ClearPlusByOnDemandContext = createContext();

export const CLEAR_PLUS_TABS = {
  FAQ: 'faq',
  ONLINE_SOLUTION: 'online-solution',
};

const ClearPlusByOnDemandProvider = ({ layoutType, children }) => {
  const { classroom: classroomConfig } = useConfig();
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [currentSubVideoId, setCurrentSubVideoId] = useState(null);
  const [activeTab, setActiveTab] = useState(CLEAR_PLUS_TABS.FAQ);
  const { data } = useMyExternalProfile({ fetchPolicy: 'cache-only' });
  const { activateInterruption, deactivateInterruption } = useClassroomInterruption();
  const { getCurrentSubVideoId } = useLearnCraftVideoPlayer();
  const restorePlayerFocus = useRestorePlayerFocus();
  const { logOpenPanel } = useFaqTracking();

  const providerName = data?.myExternalProfile?.providerName ?? '';
  const enableClearByOnDemand = classroomConfig?.clearByOnDemand?.enable;
  const isClearByOnDemand = enableClearByOnDemand && providerName === 'ondemand';

  const handleOpenDrawer = async () => {
    // NOTE: For handle use getCurrentSubVideoId video type only
    if (layoutType === LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER && getCurrentSubVideoId()) {
      const subVideoId = await getCurrentSubVideoId().split('_')[0];
      setCurrentSubVideoId(subVideoId);
    } else {
      setCurrentSubVideoId(null);
    }
    await activateInterruption();
    await setIsOpenDrawer(true);
    logOpenPanel();
  };

  const handleCloseDrawer = () => {
    deactivateInterruption();
    setIsOpenDrawer(false);
    restorePlayerFocus();
  };

  const handleChangeActiveTab = (value) => {
    setActiveTab(value);
  };

  return (
    <ClearPlusByOnDemandContext.Provider
      value={{
        isOpenDrawer,
        onOpenDrawer: handleOpenDrawer,
        onCloseDrawer: handleCloseDrawer,
        currentSubVideoId,
        isClearByOnDemand,
        activeTab,
        setActiveTab: handleChangeActiveTab,
      }}
    >
      {children}
    </ClearPlusByOnDemandContext.Provider>
  );
};

const useClearPlusByOnDemand = () => {
  const context = useContext(ClearPlusByOnDemandContext);
  if (context === undefined) {
    throw new Error('useClearPlusByOnDemand must be used within ClearPlusByOnDemandProvider');
  }
  return context;
};

export { useClearPlusByOnDemand, ClearPlusByOnDemandProvider };
export default ClearPlusByOnDemandProvider;
